import { Box, Button, Divider, makeStyles, Paper, SvgIcon, Typography } from '@material-ui/core';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DropResult } from 'react-beautiful-dnd';
import classNames from 'classnames';
import { ClientContract, ClientContractsPost, Material } from 'types';
import { onDragEndWithTwoParts } from 'components/DraggableListTwoParts/helper';
import DraggableList from 'components/DraggableListTwoParts/DraggableList';
import { colors, fonts } from 'theme';
import { CancelIcon, ValidateIcon } from 'components/Icons';
import { useBuyerById, useClientById } from 'hooks';
import { AppContext } from 'context';
import SelectWithErrorField from 'components/CustomInputs/SelectWithErrorField';
import { ItemClientContract } from './types';
import { formatClientContractsForPut } from './helper';

const useStyles = makeStyles({
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  materialTitle: {
    fontFamily: fonts.secondary,
    fontWeight: 600,
    fontSize: '14px',
    marginBottom: '8px',
  },
  colorNeutralGray: {
    color: colors.neutralGray,
  },
  collapsableDividerTop: {
    marginBottom: '22px',
  },
  collapsableDividerBottom: {
    marginBottom: '0px',
  },
  addMaterialsContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginLeft: '1em',
    alignItems: 'center',
  },
  selectMaterials: {
    background: '#FFFFFF',
    border: '1px solid #999999',
    borderRadius: '5px',
    marginLeft: '1em',
    width: '15em',
  },
  subSelectMaterials: {
    height: '2em',
  },
  activeInactive: {
    display: 'flex',
    flexDirection: 'row',
    fontFamily: fonts.secondary,
    fontWeight: 600,
    marginTop: '8px',

    '& > div': {
      display: 'flex',
      width: '50%',
      justifyContent: 'center',
    },
  },
  active: {
    color: '#8BC296',
  },
  inactive: {
    color: '#E37280',
  },
  footer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    color: colors.neutralGray,
  },
  actionButtonContainer: {
    marginRight: '1%',
  },
  actionIcon: {
    width: '40px',
    minWidth: '30px',
  },
  iconCancel: {
    color: '#2F3741',
  },
  iconValidate: {
    color: '#199EDC',
  },
});

interface CollapsableContentUpdateProps {
  clientContracts: ClientContract[] | null;
  buyerId: string;
  clientId: string;
  onCancel: () => void;
  onValidate: (clientContracts: ClientContractsPost) => void;
}

export const CollapsableContentUpdate: React.FC<CollapsableContentUpdateProps> = ({
  clientContracts,
  buyerId,
  clientId,
  onCancel,
  onValidate,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'clients' });
  const classes = useStyles();
  const { value: buyer } = useBuyerById(buyerId);
  const { value: client } = useClientById(clientId);
  const [activeContracts, setActiveContracts] = useState<ItemClientContract[]>([]);
  const [inactiveContracts, setInactiveContracts] = useState<ItemClientContract[]>([]);
  const [materialsInSelect, setMaterialsInSelect] = useState<Material[]>([]);
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();

  useEffect(() => {
    if (!buyer) return;

    const activeContracts = clientContracts
      ? clientContracts.filter((contract: ClientContract) => contract.isActive)
      : [];

    const inactiveContracts = clientContracts
      ? clientContracts.filter((contract: ClientContract) => !contract.isActive)
      : [];

    const contractMaterialIds = clientContracts?.map(clientContract => clientContract.material.shortName);
    const materialsAvailable = buyer.acceptedMaterials.filter(material => {
      return !contractMaterialIds?.includes(material.shortName);
    });

    const currentItems = Array.from(activeContracts).map((contract, index) => {
      return {
        draggableId: index.toString(),
        label: contract.material.shortName,
        id: contract.material['@id'],
        material: contract.material,
        contract: contract,
      };
    });
    let currentItemsLength = currentItems.length - 1;
    const currentItemsRight = Array.from(inactiveContracts).map(contract => {
      currentItemsLength += 1;
      return {
        draggableId: currentItemsLength.toString(),
        label: contract.material.shortName,
        id: contract.material['@id'],
        material: contract.material,
        contract: contract,
      };
    });
    setActiveContracts(currentItems);
    setInactiveContracts(currentItemsRight);
    setMaterialsInSelect(materialsAvailable);
  }, [clientContracts, buyer]);

  const onDragEndDraggableList = (dropResult: DropResult) => {
    onDragEndWithTwoParts(dropResult, activeContracts, inactiveContracts, setActiveContracts, setInactiveContracts);
  };

  const handleChangeMaterialSelect = (event: any) => {
    const materialId = event.target.value;
    const material = materialsInSelect.find(material => material['@id'] === materialId);

    if (!material) return;

    const newMaterialsInSelect = materialsInSelect.filter(material => material['@id'] !== materialId);
    const newIndex = activeContracts.length + inactiveContracts.length;

    setActiveContracts([
      ...activeContracts,
      {
        draggableId: newIndex.toString(),
        label: material.shortName,
        id: materialId,
        material,
      },
    ]);
    setMaterialsInSelect(newMaterialsInSelect);
  };

  const onClickValidate = () => {
    if (!buyer || !client || !sortingCenter) return;

    const sortingCenterId = sortingCenter['@id'];

    const activeClientContractsForPut = formatClientContractsForPut(
      activeContracts,
      sortingCenterId,
      buyer['@id'],
      client['@id'],
      true,
    );

    const inactiveClientContractsForPut = formatClientContractsForPut(
      inactiveContracts,
      sortingCenterId,
      buyer['@id'],
      client['@id'],
      false,
    );

    onValidate({
      clientContracts: [...activeClientContractsForPut, ...inactiveClientContractsForPut],
      sortingCenter: sortingCenterId,
    });
  };

  return (
    <Box>
      <Typography className={classNames(classes.materialTitle, classes.header)}>
        <div className={classes.addMaterialsContainer}>
          <div>{t('buyersList.update.addMaterial')}</div>

          <SelectWithErrorField
            choices={materialsInSelect}
            onChange={handleChangeMaterialSelect}
            className={classes.selectMaterials}
            classNameForSelect={classes.subSelectMaterials}
            value=""
            choiceDisplayKey="shortName"
            placeholder={t('buyersList.update.materialChoice')}
            error={false}
            errorMessage=""
          />
        </div>

        <div className={classes.actionButtonContainer}>
          <Button color="primary" onClick={onCancel} className={classNames(classes.iconCancel, classes.actionIcon)}>
            <SvgIcon component={CancelIcon} fontSize="small" />
          </Button>
          <Button
            color="primary"
            onClick={onClickValidate}
            className={classNames(classes.iconValidate, classes.actionIcon)}
          >
            <SvgIcon component={ValidateIcon} fontSize="small" />
          </Button>
        </div>
      </Typography>
      <Divider className={classes.collapsableDividerTop} />
      <Paper>
        <DraggableList items={activeContracts} itemsRight={inactiveContracts} onDragEnd={onDragEndDraggableList} />
      </Paper>
      <Divider className={classes.collapsableDividerBottom} />
      <Box className={classes.activeInactive}>
        <Box className={classes.active}>{t('buyersList.active')}</Box>
        <Box className={classes.inactive}>{t('buyersList.inactive')}</Box>
      </Box>
      <Box className={classes.footer}>
        <Box>{t('buyersList.update.dragDropMaterial')}</Box>
      </Box>
    </Box>
  );
};
